import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { HeroSection } from '@features/home/pages/home/ui/hero-section/hero-section';
import { LandingStory } from '@features/home/pages/home/ui/landing-story/landing-story';

const HOME_TITLE = 'Structured Innovation Workspace | Basilon';
const HOME_DESCRIPTION =
  'Turn questions, research, and evidence into prioritized innovation opportunities and structured briefs with Basilon.';
const DEFAULT_DESCRIPTION =
  'Basilon is a structured innovation workspace for research, evidence, ideas, and project briefs.';
const HOME_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const HOME_META_SELECTORS = [
  'name="twitter:card"',
  'name="twitter:title"',
  'name="twitter:description"',
  'property="og:type"',
  'property="og:site_name"',
  'property="og:locale"',
  'property="og:title"',
  'property="og:description"',
  'property="og:url"',
];

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-home',
  imports: [HeroSection, LandingStory],
  templateUrl: './home.html',
  styleUrl: './home.css',
})
export class Home {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);

  constructor() {
    this.configureSeo();
    this.destroyRef.onDestroy(() => this.resetSeo());
  }

  private configureSeo(): void {
    const canonicalUrl = new URL('/', this.document.baseURI).href;

    this.title.setTitle(HOME_TITLE);
    this.meta.updateTag({ name: 'description', content: HOME_DESCRIPTION });
    this.meta.updateTag({ name: 'robots', content: HOME_ROBOTS });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'Basilon' });
    this.meta.updateTag({ property: 'og:locale', content: 'en_US' });
    this.meta.updateTag({ property: 'og:title', content: HOME_TITLE });
    this.meta.updateTag({ property: 'og:description', content: HOME_DESCRIPTION });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary' });
    this.meta.updateTag({ name: 'twitter:title', content: HOME_TITLE });
    this.meta.updateTag({ name: 'twitter:description', content: HOME_DESCRIPTION });

    const canonical = this.getOrCreateHeadElement<HTMLLinkElement>('link', 'home-canonical');
    canonical.rel = 'canonical';
    canonical.href = canonicalUrl;

    const structuredData = this.getOrCreateHeadElement<HTMLScriptElement>(
      'script',
      'home-structured-data',
    );
    structuredData.type = 'application/ld+json';
    const organizationId = `${canonicalUrl}#organization`;
    const softwareId = `${canonicalUrl}#software`;
    const websiteId = `${canonicalUrl}#website`;

    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': organizationId,
          name: 'SageWrite ehf',
          brand: {
            '@type': 'Brand',
            name: 'Basilon',
          },
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Grettisgata 55',
            postalCode: '101',
            addressLocality: 'Reykjavík',
            addressCountry: 'IS',
          },
        },
        {
          '@type': 'WebSite',
          '@id': websiteId,
          name: 'Basilon',
          url: canonicalUrl,
          description: HOME_DESCRIPTION,
          inLanguage: 'en',
          publisher: { '@id': organizationId },
        },
        {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
          name: HOME_TITLE,
          url: canonicalUrl,
          description: HOME_DESCRIPTION,
          inLanguage: 'en',
          isPartOf: { '@id': websiteId },
          about: { '@id': softwareId },
        },
        {
          '@type': 'SoftwareApplication',
          '@id': softwareId,
          name: 'Basilon',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web browser',
          url: canonicalUrl,
          description: HOME_DESCRIPTION,
          publisher: { '@id': organizationId },
        },
      ],
    });
  }

  private resetSeo(): void {
    this.title.setTitle('Basilon');
    this.meta.updateTag({ name: 'description', content: DEFAULT_DESCRIPTION });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    HOME_META_SELECTORS.forEach((selector) => this.meta.removeTag(selector));
    this.document.getElementById('home-canonical')?.remove();
    this.document.getElementById('home-structured-data')?.remove();
  }

  private getOrCreateHeadElement<T extends HTMLElement>(tagName: string, id: string): T {
    const existing = this.document.getElementById(id) as T | null;
    if (existing) return existing;

    const element = this.document.createElement(tagName) as T;
    element.id = id;
    this.document.head.appendChild(element);
    return element;
  }
}
